import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowClockwise, X } from "@phosphor-icons/react";
import Pagination from "../Pagination";
import BusinessRowActions from "./BusinessRowActions";

const PROSPECT_STATUSES = [
  { key: "non_contacte",     label: "Non contacté" },
  { key: "email_envoye",     label: "Email envoyé" },
  { key: "a_rappeler",       label: "À rappeler" },
  { key: "interesse",        label: "Intéressé" },
  { key: "pas_interesse",    label: "Pas intéressé" },
  { key: "abonne_premium",   label: "Abonné Premium" },
  { key: "faux_numero",      label: "Faux numéro" },
  { key: "sans_email",       label: "Sans email" },
  { key: "archive",          label: "Archivé" },
];

const SORTS = [
  { key: "name_asc",      label: "Nom A → Z" },
  { key: "name_desc",     label: "Nom Z → A" },
  { key: "city_asc",      label: "Ville A → Z" },
  { key: "city_desc",     label: "Ville Z → A" },
  { key: "postal_asc",    label: "Code postal croissant" },
  { key: "postal_desc",   label: "Code postal décroissant" },
  { key: "premium_first", label: "Premium d'abord" },
  { key: "recent",        label: "Plus récents" },
  { key: "oldest",        label: "Plus anciens" },
];

const STORAGE_KEY = "mc68_admin_biz_filters";

export default function BusinessesPanel() {
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* noop */ }
    return { q: "", city: "", category: "", postal_code: "", prospect_status: "", premium_only: false, dnc_only: false, has_email: null, has_phone: null, sort: "name_asc", page: 1, limit: 30 };
  });
  const [data, setData] = useState(null);
  const [opts, setOpts] = useState({ cities: [], categories: [], category_pairs: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch { /* noop */ } }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => (params[k] === "" || params[k] === null) && delete params[k]);
      const r = await api.get("/admin/businesses", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/businesses/filter-options").then(r => setOpts(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setFilters(f => ({ ...f, [k]: v, page: k === "page" ? v : 1 }));
  const reset = () => setFilters({ q: "", city: "", category: "", postal_code: "", prospect_status: "", premium_only: false, dnc_only: false, has_email: null, has_phone: null, sort: "name_asc", page: 1, limit: 30 });

  const changeStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/businesses/${id}/prospect-status`, { status: newStatus });
      setData(d => ({...d, items: d.items.map(it => it.id === id ? {...it, prospect_status: newStatus} : it)}));
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  const { items, total } = data;

  return (
    <div data-testid="admin-businesses" className="space-y-4">
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="label-uppercase mb-1">Annuaire complet</div>
            <h2 className="font-display text-xl font-medium mb-1">Toutes les entreprises ({total.toLocaleString("fr-FR")})</h2>
            <p className="text-sm text-muted-foreground">
              Recherche, filtre, trie et gère le statut prospect des 116 000 entreprises importées.
            </p>
          </div>
          {data.dnc_total > 0 && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const { default: api } = await import("../../lib/api");
                  const r = await api.get("/admin/businesses/dnc/export.csv", { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `moncommerce68-registre-oppositions-rgpd-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                  const { toast } = await import("sonner");
                  toast.success(`Registre RGPD exporté (${data.dnc_total} opposition${data.dnc_total > 1 ? "s" : ""}).`);
                } catch (e) {
                  const { toast } = await import("sonner");
                  toast.error("Export impossible");
                }
              }}
              className="text-[11px] uppercase tracking-wider font-bold bg-card border border-border hover:border-primary hover:text-primary px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
              data-testid="dnc-export-btn"
              title="Export CSV du registre des oppositions RGPD (utile en cas d'audit CNIL)"
            >
              📥 Exporter registre RGPD (.csv)
            </button>
          )}
        </div>
        {data.dnc_total > 0 && (
          <div className="mt-3 text-xs inline-flex items-center gap-1.5 bg-muted text-foreground/80 border border-border rounded-sm px-2.5 py-1" data-testid="dnc-counter">
            <span className="font-bold tabular-nums">{data.dnc_total.toLocaleString("fr-FR")}</span>
            <span className="text-muted-foreground">
              {data.dnc_total > 1 ? "entreprises ont demandé à ne plus être contactées (RGPD)" : "entreprise a demandé à ne plus être contactée (RGPD)"}
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-sm p-4 space-y-3">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          <FilterInput label="Recherche (nom/ville/SIRET)" value={filters.q} onChange={v => update("q", v)} testid="biz-filter-q"/>
          <FilterSelect label="Ville" value={filters.city} onChange={v => update("city", v)} options={opts.cities} testid="biz-filter-city"/>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Catégorie</div>
            <select value={filters.category} onChange={e => update("category", e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" data-testid="biz-filter-cat">
              <option value="">— Toutes —</option>
              {(opts.category_pairs || []).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <FilterInput label="Code postal" value={filters.postal_code} onChange={v => update("postal_code", v)} testid="biz-filter-postal"/>
          <FilterSelect label="Statut prospect" value={filters.prospect_status} onChange={v => update("prospect_status", v)}
                        options={PROSPECT_STATUSES.map(s => s.key)} optionLabels={PROSPECT_STATUSES.reduce((acc, s) => ({...acc, [s.key]: s.label}), {})} testid="biz-filter-status"/>
          <FilterSelect label="Tri" value={filters.sort} onChange={v => update("sort", v)}
                        options={SORTS.map(s => s.key)} optionLabels={SORTS.reduce((acc, s) => ({...acc, [s.key]: s.label}), {})} testid="biz-filter-sort" required/>
          <div className="flex items-end gap-2 flex-wrap">
            <Pill on={filters.premium_only} onClick={() => update("premium_only", !filters.premium_only)} testid="biz-pill-premium">Premium uniquement</Pill>
            <Pill on={filters.dnc_only} onClick={() => update("dnc_only", !filters.dnc_only)} testid="biz-pill-dnc">🚫 DNC uniquement</Pill>
            <Pill on={filters.has_email === true} onClick={() => update("has_email", filters.has_email === true ? null : true)} testid="biz-pill-email">Avec email</Pill>
            <Pill on={filters.has_phone === true} onClick={() => update("has_phone", filters.has_phone === true ? null : true)} testid="biz-pill-phone">Avec téléphone</Pill>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={reset} className="px-3 py-2 text-sm bg-accent hover:bg-accent/70 rounded-sm flex items-center gap-1" data-testid="biz-reset"><X size={14}/> Réinitialiser</button>
            <button onClick={load} disabled={loading} className="px-3 py-2 text-sm bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white rounded-sm flex items-center gap-1 disabled:opacity-60" data-testid="biz-refresh"><ArrowClockwise size={14} className={loading ? "animate-spin" : ""}/> Rafraîchir</button>
          </div>
        </div>
      </div>

      {/* Top pagination */}
      {data.total_pages > 1 && (
        <Pagination page={data.page} totalPages={data.total_pages} total={data.total} limit={data.limit} onChange={(p) => update("page", p)} testidPrefix="biz-top"/>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-accent/20">
              <tr>
                <th className="px-3 py-2">Entreprise</th>
                <th className="px-3 py-2">Ville</th>
                <th className="px-3 py-2">CP</th>
                <th className="px-3 py-2">Catégorie</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Statut prospect</th>
                <th className="px-3 py-2 w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map(b => {
                const tier = b.premium?.tier;
                const isPremium = tier && tier !== "free";
                return (
                  <tr key={b.id} className="hover:bg-accent/20" data-testid={`biz-row-${b.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {b.name}
                        {isPremium && <span className="text-[9px] uppercase tracking-widest font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded">{tier === "premium_ai" ? "IA" : "Premium"}</span>}
                        {b.dnc && (
                          <span
                            className="text-[9px] uppercase tracking-widest font-bold bg-muted text-muted-foreground border border-border px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                            data-testid={`biz-dnc-badge-${b.id}`}
                            title={`DNC depuis ${b.dnc_at ? new Date(b.dnc_at).toLocaleDateString("fr-FR") : "—"}${b.dnc_by_telepro_code ? ` par ${b.dnc_by_telepro_code}` : ""}${b.dnc_reason ? ` · ${b.dnc_reason}` : ""}`}
                          >
                            🚫 DNC
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{b.address || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{b.city}</td>
                    <td className="px-3 py-2 text-xs font-mono">{b.postal_code}</td>
                    <td className="px-3 py-2 text-xs">{b.category}</td>
                    <td className="px-3 py-2 text-xs">
                      {b.phone && <div>📞 {b.phone}</div>}
                      {b.email && <div className="truncate max-w-[180px]">✉ {b.email}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <select value={b.prospect_status || "non_contacte"} onChange={e => changeStatus(b.id, e.target.value)} className="text-xs bg-background border border-border rounded-sm px-2 py-1" data-testid={`biz-status-${b.id}`}>
                        {PROSPECT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <BusinessRowActions
                        business={b}
                        onUpdated={(id, patch) =>
                          setData((d) => ({
                            ...d,
                            items: d.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
                          }))
                        }
                        onDeleted={(id) =>
                          setData((d) => ({
                            ...d,
                            items: d.items.filter((it) => it.id !== id),
                            total: Math.max(0, (d.total || 0) - 1),
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-10 text-center text-sm text-muted-foreground">Aucune entreprise pour ces filtres.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom pagination */}
      {data.total_pages > 1 && (
        <Pagination page={data.page} totalPages={data.total_pages} total={data.total} limit={data.limit} onChange={(p) => update("page", p)} testidPrefix="biz-bottom"/>
      )}
    </div>
  );
}

function FilterInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{label}</div>
      <div className="relative">
        <input value={value} onChange={e => onChange(e.target.value)}
               className="w-full pl-7 pr-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" data-testid={testid}/>
        <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"/>
      </div>
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, optionLabels, testid, required }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" data-testid={testid}>
        {!required && <option value="">— Tous —</option>}
        {options.map(o => <option key={o} value={o}>{optionLabels ? optionLabels[o] : o}</option>)}
      </select>
    </label>
  );
}

function Pill({ on, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-3 py-1.5 text-xs rounded-sm border ${on ? "bg-primary/10 border-primary text-primary font-semibold" : "border-border hover:bg-accent"}`}>{children}</button>
  );
}
